import { numFmtToListStyleType } from "../../docxFidelity/ooxml/numberingCore";
import { escapeHtml } from "../../docxFidelity/merge/collectBlocks";
import { markImportedOrderedListElement } from "../listNumbering";
import { stripWordListMarkerText } from "./extractMsoList";

const LEADING_MARKER_RE =
  /^(\d+[.)]|\([a-z]+\)|\([ivxlcdm]+\)|[A-Z][.)])(?:[\s\u00a0]|\.[\s\u00a0]*)*/i;

const WHITESPACE_ONLY_RE = /^[\s\u00a0.]*$/;
const LONE_MARKER_RE = /^(\d+[.)]|\([a-z]+\)|\([ivxlcdm]+\)|[A-Z][.)])$/i;

/** Word uses several conditional-comment variants around list markers. */
const SUPPORT_LISTS_BLOCK_RE =
  /<!--\s*\[if\s+!supportLists\][\s\S]*?(?:<!\[endif\]>|\[endif\]-->|<!--\[endif\]-->)/gi;

function isMsoListIgnoreElement(node: HTMLElement): boolean {
  return (node.getAttribute("style") ?? "").toLowerCase().includes("mso-list:ignore");
}

function isSupportListsComment(node: Node): boolean {
  if (node.nodeType !== Node.COMMENT_NODE) return false;
  const text = node.textContent ?? "";
  return /\[if\s+!supportLists\]/i.test(text);
}

function extractBodyFromSupportListsComment(commentText: string): string {
  const match = commentText.match(/(?:<!\[endif\]>|\[endif\]-->)([\s\S]*)$/i);
  if (!match) return "";
  return match[1].replace(/<\/p>\s*$/i, "").trim();
}

function unwrapSupportListsComments(el: HTMLElement): void {
  const comments: Comment[] = [];
  const walker = el.ownerDocument.createTreeWalker(el, NodeFilter.SHOW_COMMENT);
  let node: Node | null;
  while ((node = walker.nextNode())) {
    if (isSupportListsComment(node)) {
      comments.push(node as Comment);
    }
  }

  for (const comment of comments) {
    const body = extractBodyFromSupportListsComment(comment.textContent ?? "");
    if (body) {
      const textNode = comment.ownerDocument.createTextNode(body);
      comment.parentNode?.insertBefore(textNode, comment.nextSibling);
    }
    comment.remove();
  }
}

function removeSupportListsBlocks(el: HTMLElement): void {
  unwrapSupportListsComments(el);

  const html = el.innerHTML;
  const cleaned = html
    .replace(SUPPORT_LISTS_BLOCK_RE, "")
    .replace(/-->\s*$/g, "");
  if (cleaned !== html) {
    el.innerHTML = cleaned;
  }
}

function removeMsoListIgnoreElements(root: HTMLElement): void {
  root.querySelectorAll("span, font").forEach((node) => {
    if (node instanceof HTMLElement && isMsoListIgnoreElement(node)) {
      node.remove();
    }
  });
}

function isWhitespaceOnlyText(text: string): boolean {
  return WHITESPACE_ONLY_RE.test(text.replace(/\u00a0/g, " "));
}

function stripLeadingMarkerFromTextNode(node: Text): boolean {
  const original = node.textContent ?? "";
  const leadingWhitespace = original.match(/^\s*/)?.[0] ?? "";
  const trimmed = original.slice(leadingWhitespace.length);
  if (!trimmed) return false;

  const match = trimmed.match(LEADING_MARKER_RE);
  if (!match) return false;

  node.textContent = leadingWhitespace + trimmed.slice(match[0].length);
  return true;
}

function finalizeLeadingMarkerStrip(el: HTMLElement): void {
  const walker = el.ownerDocument.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let node: Node | null;
  while ((node = walker.nextNode())) {
    const raw = node.textContent ?? "";
    if (!raw.replace(/\u00a0/g, " ").trim()) continue;
    const leading = raw.match(/^\s*/)?.[0] ?? "";
    const trimmed = raw.slice(leading.length);
    const next = stripWordListMarkerText(trimmed);
    if (next !== trimmed) {
      node.textContent = leading + next;
    }
    return;
  }
}

/**
 * Remove Word's visible list marker from element content before wrapping in <ol>/<ul>.
 * Handles literal prefixes, supportLists comments, synthetic data-lexgat-num spans,
 * and Word's tab-span pattern: `1.<span>&nbsp;&nbsp;</span>Body text`.
 */
export function stripLeadingListMarkerFromElement(el: HTMLElement): void {
  el.querySelectorAll('[data-lexgat-num="true"]').forEach((node) => node.remove());

  removeSupportListsBlocks(el);
  removeMsoListIgnoreElements(el);

  const withoutLegacySupportLists = el.innerHTML.replace(
    /<!--\[if\s+!supportLists\]-->[\s\S]*?\[endif\]-->/gi,
    "",
  );
  if (withoutLegacySupportLists !== el.innerHTML) {
    el.innerHTML = withoutLegacySupportLists;
  }

  let changed = true;
  while (changed) {
    changed = false;

    while (el.firstChild) {
      if (el.firstChild.nodeType === Node.TEXT_NODE) {
        const textNode = el.firstChild as Text;
        const text = textNode.textContent ?? "";
        if (isWhitespaceOnlyText(text)) {
          textNode.remove();
          changed = true;
          continue;
        }
        if (LONE_MARKER_RE.test(text.trim())) {
          textNode.remove();
          changed = true;
          continue;
        }
        if (stripLeadingMarkerFromTextNode(textNode)) {
          changed = true;
          if (!(textNode.textContent ?? "").trim()) {
            textNode.remove();
            continue;
          }
        }
        break;
      }

      if (el.firstChild instanceof HTMLElement) {
        const childText = (el.firstChild.textContent ?? "").replace(/\u00a0/g, " ").trim();
        if (!childText || isWhitespaceOnlyText(childText) || LONE_MARKER_RE.test(childText)) {
          el.firstChild.remove();
          changed = true;
          continue;
        }
        stripLeadingListMarkerFromElement(el.firstChild);
        break;
      }

      break;
    }
  }

  finalizeLeadingMarkerStrip(el);

  // Clean comment debris Word leaves in text nodes after conditional blocks.
  el.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? "";
      const cleaned = text.replace(/^-->\s*/, "").replace(/\s*-->\s*$/, "");
      if (cleaned !== text) {
        node.textContent = cleaned;
      }
    }
  });
}

export interface ListRun {
  listId: number;
  ilvl: number;
  lfo: number | null;
  numFmt: string | null;
  elements: HTMLElement[];
  startValue: number | null;
  listStyleType: string | null;
  isNewInstance: boolean;
  lastCounter?: number | null;
}

export function normalizedLeadingText(el: HTMLElement): string {
  return (el.textContent ?? "").replace(/\s+/g, " ").trim();
}

export function parseLeadingCounter(text: string): number | null {
  const m = text.match(/^(\d+)[.)]/);
  return m ? parseInt(m[1], 10) : null;
}

export function prependComputedNumber(el: HTMLElement, computed: string): void {
  const label = computed.trim();
  if (!label) return;

  const existing = normalizedLeadingText(el);
  if (
    existing.startsWith(label) ||
    existing.startsWith(label.replace(/\.$/, ""))
  ) {
    return;
  }

  const prefix = `<span data-lexgat-num="true"><strong>${escapeHtml(label)}</strong></span> `;
  el.innerHTML = prefix + el.innerHTML;
}

export function stripDuplicateListPrefix(el: HTMLElement): void {
  stripLeadingListMarkerFromElement(el);
}

/**
 * Strip Word's visible numbering from items in pre-existing or newly built lists.
 */
export function stripMarkersFromListItemsInRoot(root: ParentNode): void {
  root.querySelectorAll("ol li, ul li").forEach((li) => {
    if (!(li instanceof HTMLLIElement)) return;
    stripLeadingListMarkerFromElement(li);
    li.querySelectorAll(":scope > p, :scope > h1, :scope > h2, :scope > h3, :scope > h4, :scope > h5, :scope > h6").forEach(
      (block) => {
        if (block instanceof HTMLElement) {
          stripLeadingListMarkerFromElement(block);
        }
      },
    );
  });
}

function resolveListStyle(run: ListRun): string | null {
  return run.listStyleType ?? numFmtToListStyleType(run.numFmt);
}

function createListElement(
  doc: Document,
  run: ListRun,
): HTMLOListElement | HTMLUListElement {
  const listStyle = resolveListStyle(run);
  const listTag =
    listStyle === "disc" || listStyle === "circle" || listStyle === "square"
      ? "ul"
      : "ol";

  const list = doc.createElement(listTag);
  if (listStyle) {
    list.style.listStyleType = listStyle;
  } else if (listTag === "ol") {
    list.style.listStyleType = "decimal";
  }

  if (listTag === "ol") {
    if (run.startValue != null && run.startValue > 1) {
      list.setAttribute("start", String(run.startValue));
    }
    if (run.isNewInstance) {
      markImportedOrderedListElement(list, { isNewInstance: true });
    }
  }

  return list;
}

function prepareListItemContent(el: HTMLElement, run: ListRun): void {
  const listStyle = resolveListStyle(run);
  if (listStyle) {
    stripLeadingListMarkerFromElement(el);
    return;
  }

  const text = normalizedLeadingText(el);
  const markerMatch = text.match(/^(\d+[.)]|\([a-z]+\)|\([ivxlcdm]+\)|[A-Z][.)])/i);
  if (markerMatch) {
    prependComputedNumber(el, markerMatch[1]);
  }
}

function clearDeeperLevels(
  levelLists: Map<number, HTMLOListElement | HTMLUListElement>,
  ilvl: number,
): void {
  for (const level of [...levelLists.keys()]) {
    if (level > ilvl) {
      levelLists.delete(level);
    }
  }
}

/**
 * Wrap list runs in TipTap-compatible ol/ul nodes, nesting sub-lists inside li.
 */
export function structureListRuns(doc: Document, runs: ListRun[]): number {
  let structured = 0;
  const levelLists = new Map<number, HTMLOListElement | HTMLUListElement>();

  for (const run of runs) {
    if (run.elements.length < 1) continue;
    if (run.elements.some((el) => el.closest("ol, ul"))) continue;

    clearDeeperLevels(levelLists, run.ilvl);

    const existingAtLevel = levelLists.get(run.ilvl);
    const reuseExisting =
      existingAtLevel != null && !run.isNewInstance;

    let list: HTMLOListElement | HTMLUListElement;
    if (reuseExisting) {
      list = existingAtLevel;
    } else {
      list = createListElement(doc, run);

      if (run.ilvl === 0) {
        const parent = run.elements[0].parentElement;
        if (!parent) continue;
        parent.insertBefore(list, run.elements[0]);
      } else {
        const parentList = levelLists.get(run.ilvl - 1);
        const lastLi = parentList?.lastElementChild;
        if (!(lastLi instanceof HTMLLIElement)) continue;
        lastLi.appendChild(list);
      }

      levelLists.set(run.ilvl, list);
      structured++;
    }

    for (const el of run.elements) {
      prepareListItemContent(el, run);
      const li = doc.createElement("li");
      li.innerHTML = el.innerHTML;
      stripLeadingListMarkerFromElement(li);
      li.querySelectorAll(":scope > p").forEach((block) => {
        if (block instanceof HTMLElement) {
          stripLeadingListMarkerFromElement(block);
        }
      });
      list.appendChild(li);
      el.remove();
    }
  }

  return structured;
}
